import { Box, Grid, Typography } from '@mui/material'
import { C1Button } from 'c1ui'
import { DateSelect } from 'components/core/date-select'
import { C1Icon } from 'components/core/icons/C1Icon'
import { AppUserFilter } from 'components/core/search-filter/appUser/queryRedux'
import { Minus } from 'lucide-react'
import { SearchGrantFeedRequest } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { AppUserRef } from 'pbts/c1/api/app/v1/app_user'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { useCallback, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

type GrantFeedFilterProps = {
    filter: SearchGrantFeedRequest
    onFilterChange: (filter: SearchGrantFeedRequest) => void
    appId?: string
    userId?: string
    hideAppUserFilter?: boolean
}

export const GrantFeedFilter = (
    { filter: _filter, onFilterChange: _onFilterChange, appId, userId, hideAppUserFilter = false }: GrantFeedFilterProps,
): React.ReactElement => {
    const initialFilter = useRef(_filter).current
    const [filter, setFilter] = useState<SearchGrantFeedRequest>(initialFilter)
    const [showClear, setShowClear] = useState(false)

    const clear = useCallback((): void => {
        setFilter(initialFilter)
        _onFilterChange(initialFilter)
        setShowClear(false)
    }, [_onFilterChange, initialFilter])

    const updateFilter = useCallback((newFilter: SearchGrantFeedRequest): void => {
        setFilter(newFilter)
        _onFilterChange(newFilter)
        setShowClear(true)
    }, [_onFilterChange])

    const onAppUserSelected = useCallback((appUsers): void => {
        const newFilter = SearchGrantFeedRequest.create({
            ...filter,
            appUserRefs: appUsers.map((appUser) => AppUserRef.create({
                id: appUser.id,
                appId: appUser.appId,
            })),
        })
        updateFilter(newFilter)
    }, [filter, updateFilter])

    const onAfterDateChange = useCallback((v): void => {
        const newFilter = SearchGrantFeedRequest.create({
            ...filter,
            after: v ? Timestamp.fromDate(v) : undefined,
        })

        updateFilter(newFilter)
    }, [filter, updateFilter])

    const onBeforeDateChange = useCallback((v): void => {
        const newFilter = SearchGrantFeedRequest.create({
            ...filter,
            before: v ? Timestamp.fromDate(v) : undefined,
        })

        updateFilter(newFilter)
    }, [filter, updateFilter])

    return <>
        {!hideAppUserFilter && <>
            <Grid key="app-user" item>
                <AppUserFilter selected={filter.appUserRefs} onChange={onAppUserSelected} appId={appId} userId={userId} />
            </Grid>
            <Grid key="date-range-divider" item sx={{ height: '100%' }}>
                <Box sx={{ height: '100%', borderRight: '1px solid', borderColor: 'border.primary', mx: 1 }} />
            </Grid>
        </>}
        <Grid key="date-range-label" item>
            <Typography>
                <FormattedMessage defaultMessage="Date range" id="components_grant_feed_date_range" />
            </Typography>
        </Grid>
        <Grid key="after" item>
            <DateSelect
                value={filter.after ? Timestamp.toDate(filter.after) : null}
                onChange={onAfterDateChange}
                disableFuture
            />
        </Grid>
        <Grid key="to" item>
            <C1Icon icon={Minus} size="sm" sx={{ color: 'info.main' }} />
        </Grid>
        <Grid key="before" item>
            <DateSelect
                value={filter.before ? Timestamp.toDate(filter.before) : null}
                onChange={onBeforeDateChange}
                disableFuture
            />
        </Grid>
        { showClear && <Grid key="clear" item>
            <C1Button onClick={clear} variant="text" color="info">
                <FormattedMessage id="components_clear_label" defaultMessage="Clear" />
            </C1Button>
        </Grid> }
    </>
}
